import moment from "moment";
import queryString from "query-string";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";

import { xml2js } from "../../json/xml2js";
import {
  cloneForm,
  compareForms,
  deleteFormSubmission,
  getCareTeams,
  getClientData,
  getConfig,
  getFormsySubmissionsForClient,
  getSubmission,
  handleFormChange,
  saveForm,
  sendShareEmail,
  validateForm,
} from "../../scripts/formScripts";
import {
  ArrayCheck,
  getFieldDate,
  getFieldName,
  getFieldValue,
} from "../../scripts/helperScripts";
import BackIcon from "../common/BackIcon";
import ClientFormHeader from "../common/ClientFormHeader";
import Comment from "../common/Comment";
import { Checkbox, Input } from "../common/ControlBundle";
import FormButtonBar from "../common/FormButtonBar";
import ShareModal from "../common/ShareModal";
import Signature from "../common/Signature";
import { getAttachments } from "../services/soar";
import { getSchema } from "./joi/validation";
import FileUpload from "../common/FileUpload";

function KYTFCF032() {
  const location = useLocation();
  const navigate = useNavigate();
  const initialProgram = location.state?.selectedProgram;

  //Get route and query string params
  const { clientid, formid, submissionid } = useParams();
  const qs = queryString.parse(location.search);
  console.log("Params", clientid, formid, submissionid);
  console.log("QS:", qs);

  //Get logged in user info
  const safyInfo = JSON.parse(localStorage.getItem("safyInfo"));
  //The complete Form Submission object with form data
  const [formsyDetails, setFormsyDetails] = useState({});
  const [formsyConfig, setFormsyConfig] = useState({});
  const [loadingConfig, setLoadingConfig] = useState(false);
  //The form data and previous forms
  const [data, setData] = useState({});
  const [allFormSubmissions, setAllFormSubmissions] = useState([]);
  //Touched fields
  const [touched, setTouched] = useState({});
  const [isSaveInProgress, setIsSaveInProgress] = useState(false);
  //Errors
  const [errors, setErrors] = useState({});
  //Client, Program and CareTeam Info
  const [clientData, setClientData] = useState({});
  const [careTeams, setCareTeams] = useState([]);

  //Signatures State Property
  const [signatures, setSignatures] = useState([]);
  //Show Signature Modal
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  //Comments state Property
  const [comments, setComments] = useState();
  //Show Comments Modal
  const [showComment, setShowComment] = useState(false);
  //Show Share Modal
  const [showShare, setShowShare] = useState(false);
  //Show Form Level (not field specific) Comments
  const [showFormComment, setShowFormComment] = useState(false);

  const [doCompare, setDoCompare] = useState(false);
  const [timeoutId, setTimeoutId] = useState();

  const pdfRef = useRef(true);
  const isMountedRef = useRef(true);

  //Get validation schema
  const schema = getSchema(formid);
  console.log("Schema Config: ", schema);

  const selectedProgramId = useMemo(() => {
    return data.SelectedProgram?.["@value"];
  }, [data.SelectedProgram]);

  const formDisabled = useMemo(() => {
    return formsyDetails.Active === "0" || formsyDetails.FinalSigned === "1";
  }, [formsyDetails]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      pdfRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (
      !initialProgram?.ccx_client_programid ||
      !data.SelectedProgram ||
      data.SelectedProgram["@value"]
    ) {
      return;
    }

    const obj = { ...data };
    obj.SelectedProgram["@value"] = initialProgram.ccx_client_programid;
    setData(obj);

    if (formsyDetails.ClientProgramId === initialProgram.ccx_client_programid) {
      return;
    }

    const details = { ...formsyDetails };
    details.ClientProgramId = initialProgram.ccx_client_programid;
    details.ClientProgramName = getFieldName(initialProgram.ccx_programunit);
    setFormsyDetails(details);
  }, [initialProgram, selectedProgramId, data, formsyDetails]);

  //Get Client Info
  useEffect(() => {
    if (!formid || !clientid) {
      return;
    }

    const loadClientInfo = async () => {
      const response = await Promise.all([
        getFormsySubmissionsForClient(formid, clientid),
        getCareTeams(clientid),
      ]);

      if (!isMountedRef.current || !response) {
        return;
      }

      setAllFormSubmissions(response[0]);
      setCareTeams(response[1]);
    };

    loadClientInfo();

    if (submissionid !== undefined) {
      const loadSubmission = async () => {
        const response = await getSubmission(submissionid);

        if (!isMountedRef.current || !response) {
          return;
        }

        setData(response.Data);
        setFormsyDetails(response.FormsyDetails);

        if (response.Comments) {
          setComments(response.Comments);
        }

        if (response.Signatures) {
          setSignatures(response.Signatures);
        }
      };

      loadSubmission();
    }
  }, [formid, clientid, submissionid]);

  useEffect(() => {
    if (
      !formid ||
      !clientid ||
      Object.keys(formsyConfig).length > 0 ||
      loadingConfig ||
      !safyInfo?.user
    ) {
      return;
    }

    const loadConfig = async () => {
      setLoadingConfig(true);
      const response = await getConfig(formid);

      if (!isMountedRef.current || !response) {
        return;
      }

      setFormsyConfig(response);
      const clientDataResponse = await getClientData(
        clientid,
        submissionid !== undefined,
        `${response.FormName}${
          response.VersionIntId > 1 ? `v${response.VersionIntId}` : ""
        }`
      );

      if (!isMountedRef.current || !clientDataResponse) {
        return;
      }

      setClientData(clientDataResponse);

      if (!submissionid) {
        //Set Formsy Details
        const newForm = {
          SubmissionId: "",
          FormId: response.FormId,
          FormVersionId: formid,
          ContactId: clientid,
          ContactName: clientDataResponse.client.fullName,
          ClientProgramId: "",
          ClientProgramName: "",
          TertiaryObjectId: "",
          TertiaryObjectName: "",
          FormResults: {
            results: {},
          },

          Comments: "",
          Signatures: "",
          CreatedOn: "",
          CreatedById: safyInfo.user.azureId,
          CreatedByName: safyInfo.user.displayName,
          ModifiedById: safyInfo.user.azureId,
          ModifiedByName: safyInfo.user.displayName,
          FinalSigned: "0",
          FinalSignedDate: "",
          Active: "1",
        };
        setFormsyDetails(newForm);
        setData(response.FormConfiguration[0].config[0]);
        setLoadingConfig(false);

        console.log("New Form:", newForm);
        console.log(
          "loadConfig setData:",
          response.FormConfiguration[0].config[0]
        );
      }
    };

    loadConfig();
  }, [
    loadingConfig,
    formid,
    clientid,
    safyInfo?.user,
    formsyConfig,
    submissionid,
  ]);

  useEffect(() => {
    if (!submissionid || !!formsyDetails.SubmissionId) {
      return;
    }

    const obj = { ...formsyDetails };
    obj.SubmissionId = submissionid;
    setFormsyDetails(obj);
  }, [submissionid, formsyDetails]);

  const helperConfiguration = useMemo(() => {
    try {
      return JSON.parse(formsyConfig.HelperConfiguration[0]);
    } catch {
      const defaultConfig = {
        buttonBarConfig: {
          sign: {
            display: false,
            initialSignatureType: "full",
          },
          clone: {
            display: false,
          },
          delete: {
            display: false,
          },
          share: {
            display: false,
          },
          comment: {
            display: false,
          },
        },
        signatureType: "full",
        finalSignType: "Provider",
      };

      return defaultConfig;
    }
  }, [formsyConfig.HelperConfiguration]);

  const getSelectedProgramName = (dataObj, cData) => {
    if (!dataObj.SelectedProgram || !cData.programs) {
      return "";
    }

    let name = getFieldName(dataObj.SelectedProgram);
    if (name) {
      return name;
    } else {
      const selectedProgram = cData.programs.filter(
        (p) => p.ccx_client_programid === getFieldValue(dataObj.SelectedProgram)
      )[0];

      if (selectedProgram) {
        name = getFieldName(selectedProgram.ccx_programunit);
        const programDates = ` (${getFieldDate(
          selectedProgram.ccx_admitdate
        )} - ${
          selectedProgram.ccx_dischargedate
            ? getFieldDate(selectedProgram.ccx_dischargedate)
            : "no date"
        })`;
        return name + programDates;
      } else {
        return "";
      }
    }
  };

  //Form validation
  const validate = () => {
    const results = validateForm(data, schema);
    setErrors(results);
    return results;
  };

  const handleChange = ({ currentTarget: input }) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    const result = handleFormChange(input, data, schema, errors);
    setData(result.Data);
    setErrors(result.Errors);

    if (!submissionid) {
      return result.Data;
    }

    const touchedObj = { ...touched, [input.name]: true };
    const touchedFields = Object.keys(touchedObj).filter(
      (field) => touchedObj[field]
    );

    if (touchedFields.length >= 5) {
      if (isSaveInProgress) {
        const toId = setTimeout(() => {
          touchedFields.forEach((field) => (touchedObj[field] = false));
          handleSubmit(result.Data, null, null, null, true);
        }, 20000);

        setTimeoutId(toId);
      } else {
        clearTimeout(timeoutId);
        handleSubmit(result.Data, null, null, null, true);
        touchedFields.forEach((field) => (touchedObj[field] = false));
      }
    } else {
      const toId = setTimeout(() => {
        if (isSaveInProgress) {
          const toId2 = setTimeout(() => {
            touchedFields.forEach((field) => (touchedObj[field] = false));
            handleSubmit(result.Data, null, null, null, true);
          }, 20000);

          setTimeoutId(toId2);
        } else {
          touchedFields.forEach((field) => (touchedObj[field] = false));
          handleSubmit(result.Data, null, null, null, true);
        }
      }, 20000);

      setTimeoutId(toId);
    }

    setTouched(touchedObj);
    return result.Data;
  };

  ////SAVE
  const handleSubmit = async (
    dataOverride,
    formsyDetailsOverride,
    commentsOverride,
    signaturesOverride,
    autoSave = false
  ) => {
    if (isSaveInProgress) {
      return;
    }

    const dataObj = dataOverride || data;
    const formsyDetailsObj = formsyDetailsOverride || formsyDetails;
    const commentsObj =
      commentsOverride === undefined ? undefined : commentsOverride || comments;
    const signaturesObj = signaturesOverride || signatures;

    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    setTouched({});
    const errs = { ...errors };

    if (!dataObj.SelectedProgram?.["@value"]) {
      errs.SelectedProgram = '"Selected Program" is required';
      setErrors(errs);
      return;
    }

    delete errs.SelectedProgram;
    setErrors(errs);
    setIsSaveInProgress(true);

    try {
      const submissionResponse = await saveForm(
        dataObj,
        formsyDetailsObj,
        commentsObj,
        signaturesObj,
        navigate,
        location,
        autoSave
      );

      if (!isMountedRef.current || !submissionResponse?.Id) {
        return;
      }

      const newDetails = { ...formsyDetailsObj };
      newDetails.ModifiedOn = submissionResponse.LastModifiedOn;
      newDetails.ModifiedById = safyInfo.user.azureId;
      newDetails.ModifiedByName = safyInfo.user.displayName;

      if (newDetails.SubmissionId === "") {
        newDetails.SubmissionId = submissionResponse?.Id;
      }

      setFormsyDetails(newDetails);
    } finally {
      setIsSaveInProgress(false);
    }
  };

  ////CLONE
  const handleOnClone = (event) => {
    event.preventDefault();
    cloneForm(
      data,
      formsyDetails,
      comments,
      signatures,
      navigate,
      location,
      formsyConfig
    );
    setSignatures([]);
  };

  ////DELETE
  const handleDelete = (e) => {
    e.preventDefault();
    if (submissionid) {
      deleteFormSubmission(
        data,
        formsyDetails,
        comments,
        signatures,
        navigate,
        location
      );
    } else {
      alert("The form could not be deleted because it has not been saved");
    }
  };

  ////SHARE
  const toggleShareModal = () => {
    setShowShare(!showShare);
  };

  const handleShareSend = (recipientList, shareComments) => {
    sendShareEmail(
      recipientList,
      shareComments,
      location.pathname,
      data,
      formsyDetails,
      comments,
      signatures,
      formsyConfig.FormDescription
    );
  };

  ////COMMENTS FUNCTIONALITY
  const [commentToEdit, setCommentToEdit] = useState();

  const toggleCommentModal = (editComment) => {
    if (!showComment && editComment) setCommentToEdit(editComment);
    setShowComment(!showComment);
  };

  const handleComments = ({ action, fieldName, comment, acknowledge }) => {
    if (action === "ADD") {
      setShowComment(true);
    } else if (comment !== undefined) {
      if (action === "EDIT") {
        toggleCommentModal(comment);
      } else if (action === "DELETE") {
        handleDeleteComment(comment);
      } else if (action === "ACKNOWLEDGE" && acknowledge !== undefined) {
        handleAcknowledgeComment(comment, acknowledge);
      }
    } else if (action === "GET") {
      return parsedComments.filter(
        (comment) => comment.field._text === fieldName
      );
    }
  };

  const commentFields = () => {
    let commentOptions = [];
    commentOptions.push({ _id: "EntireForm", name: "Entire Form----" });
    for (const property in data) {
      if (
        data[property]["@type"] !== "section" &&
        data[property]["@type"] !== "label" &&
        data[property]["@type"] !== "User" &&
        data[property]["@type"] !== "hidden"
      ) {
        const option = {
          _id: property,
          name:
            data[property]["@label"] !== undefined &&
            data[property]["@label"] !== ""
              ? data[property]["@label"]
              : property,
        };
        commentOptions.push(option);
      }
    }
    return commentOptions;
  };

  const parsedComments = useMemo(() => {
    if (!comments) {
      return [];
    }

    const commentObj = xml2js(`<comments type="array">${comments}</comments>`, {
      compact: true,
      spaces: 4,
    });

    return ArrayCheck(commentObj.comments.comment);
  }, [comments]);

  const onCommentSave = (comment, commentToEdit) => {
    let formsyComments;
    if (commentToEdit) {
      const commentXml = `<comment><createdby>${
        commentToEdit.createdby._text
      }</createdby><datetime>${commentToEdit.datetime._text}</datetime><field>${
        commentToEdit.field._text
      }</field><comment>${commentToEdit.comment._text}</comment>${
        commentToEdit.acknowledgedby
          ? `<acknowledgedby>${commentToEdit.acknowledgedby._text}</acknowledgedby><acknowledgedon>${commentToEdit.acknowledgedon._text}</acknowledgedon>`
          : ""
      }</comment>`;
      console.log("CurrentCommentsXML: ", comments);
      console.log("CommentToReplace: ", commentXml);
      console.log("CommentToEdit: ", commentToEdit);
      console.log("CommentToEditXML: ", comment);

      formsyComments =
        comments !== undefined
          ? comments.replace(commentXml, comment)
          : comment;
      setComments(formsyComments);
    } else {
      formsyComments = comments !== undefined ? comments + comment : comment;
      setComments(formsyComments);
    }

    setShowComment(false);
    handleSubmit(null, null, formsyComments, null);
    setCommentToEdit(undefined);
  };

  const handleDeleteComment = (comment) => {
    const commentXml = `<comment><createdby>${
      safyInfo.user.displayName
    }</createdby><datetime>${comment.datetime._text}</datetime><field>${
      comment.field._text
    }</field><comment>${comment.comment._text}</comment>${
      comment.acknowledgedby
        ? `<acknowledgedby>${comment.acknowledgedby._text}</acknowledgedby><acknowledgedon>${comment.acknowledgedon._text}</acknowledgedon>`
        : ""
    }</comment>`;

    let formsyComments = comments.replace(commentXml, "");
    if (formsyComments === "") formsyComments = undefined;
    setComments(formsyComments);
    handleSubmit(null, null, formsyComments, null);
  };

  const toggleCommentToEdit = (comment) => {
    setCommentToEdit(comment);
    setShowComment(true);
  };

  const handleAcknowledgeComment = (comment, acknowledge) => {
    if (acknowledge) {
      const oldCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment></comment>`;
      const newCommentXML = `<comment><createdby>${
        comment.createdby._text
      }</createdby><datetime>${comment.datetime._text}</datetime><field>${
        comment.field._text
      }</field><comment>${comment.comment._text}</comment><acknowledgedby>${
        safyInfo.user.displayName
      }</acknowledgedby><acknowledgedon>${moment().format(
        "L"
      )} ${moment().format("LT")}</acknowledgedon></comment>`;
      const formsyComments = comments.replace(oldCommentXML, newCommentXML);
      setComments(formsyComments);
      handleSubmit(null, null, formsyComments, null);
    } else {
      const oldCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment><acknowledgedby>${comment.acknowledgedby._text}</acknowledgedby><acknowledgedon>${comment.acknowledgedon._text}</acknowledgedon></comment>`;
      const newCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment></comment>`;
      const formsyComments = comments.replace(oldCommentXML, newCommentXML);
      setComments(formsyComments);
      handleSubmit(null, null, formsyComments, null);
    }
  };

  const handleCommentNavigation = (fieldName) => {
    const element = document.getElementById(fieldName);
    if (element.type === "checkbox")
      scrollTo(
        document.getElementById(fieldName).parentElement.parentElement
          .parentElement.offsetTop - 50,
        () => {
          document.getElementById(fieldName).focus();
        }
      );
    else
      scrollTo(document.getElementById(fieldName).offsetTop - 50, () => {
        document.getElementById(fieldName).focus();
      });
  };

  const renderComments = () => {
    return parsedComments
      .filter(
        (comment) =>
          comment.field._text === "EntireForm" ||
          comment.field._text === "Entire Form----"
      )
      .map((comment, index) => (
        <div className="mt-2" key={`form-comment-${index}`}>
          {showFormComment && (
            <div
              className={`${
                comment.acknowledgedby ? "bg-safy3-50" : "bg-warning"
              } p-2 m-2 rounded d-print-none`}
              title={
                comment.acknowledgedby
                  ? `Acknowledged by: ${comment.acknowledgedby._text} ${comment.acknowledgedon._text}`
                  : ""
              }
            >
              <span className="h6">
                {comment.createdby._text ===
                  JSON.parse(localStorage.getItem("safyInfo")).user
                    .displayName && (
                  <>
                    <i
                      title="Delete Comment"
                      className="far fa-trash-alt text-white me-2"
                      onClick={() => {
                        confirmAlert({
                          title: "Delete Comment",
                          message:
                            "Are you sure you want to delete this comment?",
                          buttons: [
                            {
                              label: "Yes",
                              onClick: () => {
                                handleDeleteComment(comment);
                              },
                            },
                            {
                              label: "No",
                              onClick: () => {
                                /* do nothing */
                              },
                            },
                          ],
                        });
                      }}
                    ></i>
                    {!comment.acknowledgedby && (
                      <i
                        title="Edit Comment"
                        className="fas fa-pen text-white me-2"
                        onClick={(e) => {
                          toggleCommentToEdit(comment);
                        }}
                      ></i>
                    )}
                  </>
                )}
                {comment.acknowledgedby && (
                  <i
                    className="far fa-check-square text-white me-2"
                    onClick={() => {
                      if (
                        comment.acknowledgedby._text ===
                        JSON.parse(localStorage.getItem("safyInfo")).user
                          .displayName
                      )
                        handleAcknowledgeComment(comment, false);
                    }}
                  ></i>
                )}
                {!comment.acknowledgedby && (
                  <i
                    className="far fa-square text-white me-2"
                    onClick={() => {
                      handleAcknowledgeComment(comment, true);
                    }}
                    title="Acknowledge Comment"
                  ></i>
                )}
                <br />
                {`${comment.createdby._text} - ${comment.datetime._text}`}
              </span>
              <br />
              Regarding:{" "}
              {comment.field._text !== "EntireForm" &&
              comment.field._text !== "Entire Form----" ? (
                <button
                  className="btn btn-link"
                  onClick={() => {
                    setTimeout(() => {
                      handleCommentNavigation(comment.field._text);
                    }, 100);
                  }}
                >
                  {comment.field._text}
                </button>
              ) : (
                <>{comment.field._text}</>
              )}
              <br />
              {comment.comment._text}
            </div>
          )}
        </div>
      ));
  };

  function scrollTo(offset, callback) {
    const fixedOffset = offset.toFixed();
    const onScroll = function () {
      if (window.pageYOffset.toFixed() === fixedOffset) {
        window.removeEventListener("scroll", onScroll);
        callback();
      }
    };

    window.addEventListener("scroll", onScroll);
    onScroll();
    window.scrollTo({
      top: offset,
      behavior: "smooth",
    });
  }

  //SIGNATURE FUNCTIONALITY
  const toggleSignatureModal = (e) => {
    e.preventDefault();
    if (!isSaveInProgress) setShowSignatureModal(true);
  };

  const saveSignature = async (signatureData) => {
    console.log("Signature Data:", signatureData);

    let sigArray = [];
    if (Array.isArray(signatureData)) {
      sigArray = signatureData;
    } else {
      sigArray.push(signatureData);
    }
    setSignatures(sigArray);

    const formsyDetailsObj = { ...formsyDetails };

    if (formsyDetailsObj.FinalSigned !== "1" && checkFinalSigned(sigArray)) {
      console.log("Final Signed");
      formsyDetailsObj.FinalSigned = "1";

      if (
        formsyDetailsObj.FinalSignedDate === "" ||
        formsyDetailsObj.FinalSignedDate === "1900-01-01T00:00:00"
      ) {
        formsyDetailsObj.FinalSignedDate = new Date();
      }

      setFormsyDetails(formsyDetailsObj);
      setComments(undefined);
    }

    handleSubmit(
      null,
      formsyDetailsObj,
      formsyDetailsObj.FinalSigned === "1" ? undefined : null,
      sigArray
    );
  };

  const checkFinalSigned = (sigArray) => {
    const requiredSignatureTypes = helperConfiguration.finalSignType.split(",");

    const capturedSignatureTypes = sigArray.map((sig) => sig.signatureType);
    console.log("requiredSignatureTypes: ", requiredSignatureTypes);
    console.log("capturedSignatureTypes: ", capturedSignatureTypes);

    const missingSigTypes = requiredSignatureTypes.filter(
      (type) => !capturedSignatureTypes.includes(type)
    );

    return missingSigTypes.length === 0;
  };

  //COMPARE
  const compare = (field) => {
    if (doCompare)
      return compareForms(field, data, allFormSubmissions, formsyDetails);
  };

  const toggleCompare = () => {
    setDoCompare(!doCompare);
  };

  //CUSTOM
  const [fileAttachments, setFileAttachments] = useState([]);

  const loadFiles = useCallback(async () => {
    if (submissionid) {
      const response = await getAttachments(submissionid);
      if (response?.data.length > 0) {
        setFileAttachments(response.data);
      }
    }
  }, [submissionid]);

  useEffect(() => {
    loadFiles();
  }, [loadFiles]);

  useEffect(() => {
    if (
      Object.keys(data).length > 0 &&
      data.ReasonForPlacement["@value"] === undefined &&
      clientData.programs !== undefined &&
      clientData.programs.length > 0
    ) {
      const obj = { ...data };

      obj.ReasonForPlacement[
        "@value"
      ] = `${clientData.programs[0]["con.firstname"]} is in need of a stable therapeutic foster home environment which is able to meet daily living needs and assist in age appropriate development, is able to meet yearly medical requirements of annual physical, hearing, vision and dental exams/screens, is able to participate in treatment planning and implementation, is able to assist and/or provide permanency for ${clientData.programs[0]["con.firstname"]} and provide an environment which is favorable to health healing and growth`;

      obj.Goal1[
        "@value"
      ] = `${clientData.programs[0]["con.firstname"]} will adjust to new foster home setting`;
      obj.Objectives1[
        "@value"
      ] = `1). ${clientData.programs[0]["con.firstname"]} will learn the rules and routines of the current home and will comply and accept these rules/routines.
2). ${clientData.programs[0]["con.firstname"]} will express feelings of acceptance into new home and family`;
      obj.Actions1[
        "@value"
      ] = `1). Foster parents will provide structure and routine within the household to assist with ${clientData.programs[0]["con.firstname"]}'s adjustment into the home
2). Foster Parents will provide explain house rules and routines to ${clientData.programs[0]["con.firstname"]} and will assist ${clientData.programs[0]["con.firstname"]} in learning these daily
3). Foster parents will incorporate ${clientData.programs[0]["con.firstname"]} into the family setting by spending time with ${clientData.programs[0]["con.firstname"]} to determine skills and strengths which can be included into family dynamics/structure`;
      obj.Outcomes1[
        "@value"
      ] = `${clientData.programs[0]["con.firstname"]} will feel comfortable and safe in new foster home setting`;

      obj.Goal2[
        "@value"
      ] = `${clientData.programs[0]["con.firstname"]} will successfully acclimate to surroundings, children in the home and in the neighborhood`;
      obj.Objectives2[
        "@value"
      ] = `${clientData.programs[0]["con.firstname"]} will cooperate and participate with services provided to assist with overall development and functioning`;
      obj.Actions2[
        "@value"
      ] = `Foster parents will involve ${clientData.programs[0]["con.firstname"]} in family functions/outings`;
      obj.Outcomes2[
        "@value"
      ] = `${clientData.programs[0]["con.firstname"]} will be able to maintain in the home/get along with other children in the home/be respectful to others`;

      obj.Goal3[
        "@value"
      ] = `${clientData.programs[0]["con.firstname"]}'s physical and mental health needs will be met`;
      obj.Objectives3[
        "@value"
      ] = `1). ${clientData.programs[0]["con.firstname"]} will notify foster parents upon becoming ill
2). Foster parents will be aware of ${clientData.programs[0]["con.firstname"]}'s typical/baseline behaviors and will address any and all physical and mental health needs`;
      obj.Actions3[
        "@value"
      ] = `Foster parents will schedule appointments to address presenting concerns as they arise`;
      obj.Outcomes3[
        "@value"
      ] = `${clientData.programs[0]["con.firstname"]}'s physical and mental health needs will be met`;

      obj.Strategies[
        "@value"
      ] = `Foster parents will spend time with ${clientData.programs[0]["con.firstname"]} learning their preferences for foods and other personal needs as well as expectations of placement. Foster parents will inquire as to ${clientData.programs[0]["con.firstname"]}'s preferred methods of self-calming when upset and will incorporate those into parenting practices as well as continue to identify and promote other appropriate self-calming behaviors.`;

      obj.SupervisionNeeds[
        "@value"
      ] = `${clientData.programs[0]["con.firstname"]} will be supervised at all times during the first 30 days of placement. Foster parents and SAFY will re-evaluate supervision after 30 days.`;

      obj.DischargePlanning[
        "@value"
      ] = `Discharge planning will occur at least one month prior to transition from SAFY care. At that time, all ${clientData.programs[0]["con.firstname"]}'s medical, psychiatric and personal needs will be addressed and referrals will be made.`;

      setData(obj);
    }
  }, [data, clientData]);

  const isHighRisk = useMemo(() => {
    return (
      getFieldValue(data.SuicidalIdeation) === "true" ||
      getFieldValue(data.HomicidalBehaviors) === "true" ||
      getFieldValue(data.SelfHarm) === "true" ||
      getFieldValue(data.AWOL) === "true" ||
      getFieldValue(data.PropertyDestruction) === "true" ||
      getFieldValue(data.PhysicalAggression) === "true" ||
      getFieldValue(data.VerbalAggression) === "true" ||
      getFieldValue(data.SubstanceUse) === "true" ||
      getFieldValue(data.UnsafeSleep) === "true" ||
      getFieldValue(data.UnsafeActsAnimals) === "true" ||
      getFieldValue(data.UnsafeSexualBehaviors) === "true" ||
      getFieldValue(data.TraffickingVictim) === "true" ||
      getFieldValue(data.OtherBehavior) === "true"
    );
  }, [data]);

  ////UI
  return (
    <>
      <div className="mb-2">
        <BackIcon />
      </div>
      <div className={!submissionid ? "watermarked" : ""} ref={pdfRef}>
        <ClientFormHeader
          formTitle={formsyConfig.FormDescription}
          clientData={clientData.client}
          formsyDetails={formsyDetails}
        ></ClientFormHeader>
        <FormButtonBar
          config={
            formsyConfig.HelperConfiguration
              ? JSON.parse(formsyConfig.HelperConfiguration[0]).buttonBarConfig
              : {
                  sign: {
                    display: false,
                    initialSignatureType: "supervisor",
                  },
                  clone: {
                    display: false,
                  },
                  delete: {
                    display: false,
                  },
                  share: {
                    display: false,
                  },
                  comment: {
                    display: false,
                  },
                }
          }
          onSave={handleSubmit}
          onClone={handleOnClone}
          onDelete={handleDelete}
          onSignClicked={toggleSignatureModal}
          onComment={toggleCommentModal}
          onCompare={toggleCompare}
          onShare={toggleShareModal}
          signed={signatures.length > 0}
          finalSigned={formsyDetails.FinalSigned === "1"}
          validate={validate}
          pdfRef={pdfRef}
          filename={`${formsyConfig.FormDescription}`}
          newestVersion={
            formsyConfig.VersionIntId === formsyConfig.LatestVersionIntId
          }
        />
        <div className="py-2 border-bottom border-dark">
          <b>Selected Client Program:</b>{" "}
          {getSelectedProgramName(data, clientData)}
        </div>
        {renderComments()}
        {parsedComments.filter(
          (comment) =>
            comment.field._text === "EntireForm" ||
            comment.field._text === "Entire Form----"
        ).length > 0 && (
          <i
            className="fas fa-comment-alt text-warning float-end mb-2 ms-2"
            onClick={() => setShowFormComment(!showFormComment)}
          ></i>
        )}
        <div className="mt-4">
          {data && Object.keys(data).length > 0 && (
            <>
              <Input
                name="DateCompleted"
                label="Date Completed"
                error={errors.DateCompleted}
                value={data.DateCompleted["@value"]}
                onChange={handleChange}
                identical={compare("DateCompleted")}
                commentsHandler={handleComments}
                disabled={formDisabled}
                type="date"
              />
              <div className="h6">
                <u>Assessment</u>
              </div>
              <Input
                name="ReasonForPlacement"
                label="Reason for Placement"
                error={errors.ReasonForPlacement}
                value={data.ReasonForPlacement["@value"]}
                onChange={handleChange}
                identical={compare("ReasonForPlacement")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <Input
                name="YouthStrengths"
                label="Youth Strengths"
                error={errors.YouthStrengths}
                value={data.YouthStrengths["@value"]}
                onChange={handleChange}
                identical={compare("YouthStrengths")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <Input
                name="PresentingIssues"
                label="Presenting Issues"
                error={errors.PresentingIssues}
                value={data.PresentingIssues["@value"]}
                onChange={handleChange}
                identical={compare("PresentingIssues")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              High Risk Behaviors:
              <div className="container border pt-3">
                <div className="row row-cols-1 row-cols-sm-1 row-cols-md-3 row-cols-lg-3 row-cols-xl-3">
                  <div className="col">
                    <Checkbox
                      name="SuicidalIdeation"
                      label="Suicidal Ideation"
                      checked={data.SuicidalIdeation["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                  </div>
                  <div className="col">
                    <Checkbox
                      name="HomicidalBehaviors"
                      label="Homicidal Behaviors"
                      checked={data.HomicidalBehaviors["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                  </div>
                  <div className="col">
                    <Checkbox
                      name="SelfHarm"
                      label="Self-Harm"
                      checked={data.SelfHarm["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                  </div>
                  <div className="col">
                    <Checkbox
                      name="AWOL"
                      label="AWOL"
                      checked={data.AWOL["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                  </div>
                  <div className="col">
                    <Checkbox
                      name="PropertyDestruction"
                      label="Property Destruction"
                      checked={data.PropertyDestruction["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                  </div>
                  <div className="col">
                    <Checkbox
                      name="PhysicalAggression"
                      label="Physical Aggression"
                      checked={data.PhysicalAggression["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                  </div>
                  <div className="col">
                    <Checkbox
                      name="VerbalAggression"
                      label="Verbal Aggression"
                      checked={data.VerbalAggression["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                  </div>
                  <div className="col">
                    <Checkbox
                      name="SubstanceUse"
                      label="Substance Use"
                      checked={data.SubstanceUse["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                  </div>
                  <div className="col">
                    <Checkbox
                      name="UnsafeSleep"
                      label="Unsafe Sleep Behaviors"
                      checked={data.UnsafeSleep["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                  </div>
                  <div className="col">
                    <Checkbox
                      name="UnsafeActsAnimals"
                      label="Unsafe Acts Towards Animals"
                      checked={data.UnsafeActsAnimals["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                  </div>
                  <div className="col">
                    <Checkbox
                      name="UnsafeSexualBehaviors"
                      label="Unsafe Sexual Behaviors"
                      checked={data.UnsafeSexualBehaviors["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                  </div>
                  <div className="col">
                    <Checkbox
                      name="TraffickingVictim"
                      label="Victim of Human Trafficking"
                      checked={data.TraffickingVictim["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                  </div>
                  <div className="col">
                    <Checkbox
                      name="OtherBehavior"
                      label="Other"
                      checked={data.OtherBehavior["@value"] === "true"}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      divClassName="ms-3"
                    />
                    {data.OtherBehavior["@value"] === "true" ? (
                      <Input
                        name="OtherBehaviorDetails"
                        error={errors.OtherBehaviorDetails}
                        value={data.OtherBehaviorDetails["@value"]}
                        onChange={handleChange}
                        identical={compare("OtherBehaviorDetails")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    ) : null}
                  </div>
                </div>
              </div>
              {isHighRisk ? (
                <div className="h6 mt-2">
                  Please remember to complete a{" "}
                  <Link
                    to={`/SafetyPlanv3/5BA3CED4-21A9-EE11-AD37-DC98408D5418/${clientid}`}
                    state={{
                      selectedProgram: getFieldValue(data.SelectedProgram),
                    }}
                  >
                    Safety Plan
                  </Link>
                  .
                </div>
              ) : null}
              <div className="h6">
                <u>Treatment Plan</u>
              </div>
              <Input
                name="Goal1"
                label="Goal #1"
                error={errors.Goal1}
                value={data.Goal1["@value"]}
                onChange={handleChange}
                identical={compare("Goal1")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <Input
                name="Objectives1"
                label="Objectives"
                error={errors.Objectives1}
                value={data.Objectives1["@value"]}
                onChange={handleChange}
                identical={compare("Objectives1")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <Input
                name="Actions1"
                label="Planned Actions"
                error={errors.Actions1}
                value={data.Actions1["@value"]}
                onChange={handleChange}
                identical={compare("Actions1")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <Input
                name="Outcomes1"
                label="Expected Outcomes"
                error={errors.Outcomes1}
                value={data.Outcomes1["@value"]}
                onChange={handleChange}
                identical={compare("Outcomes1")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <br />
              <Input
                name="Goal2"
                label="Goal #2"
                error={errors.Goal2}
                value={data.Goal2["@value"]}
                onChange={handleChange}
                identical={compare("Goal2")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <Input
                name="Objectives2"
                label="Objectives"
                error={errors.Objectives2}
                value={data.Objectives2["@value"]}
                onChange={handleChange}
                identical={compare("Objectives2")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <Input
                name="Actions2"
                label="Planned Actions"
                error={errors.Actions2}
                value={data.Actions2["@value"]}
                onChange={handleChange}
                identical={compare("Actions2")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <Input
                name="Outcomes2"
                label="Expected Outcomes"
                error={errors.Outcomes2}
                value={data.Outcomes2["@value"]}
                onChange={handleChange}
                identical={compare("Outcomes2")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <br />
              <Input
                name="Goal3"
                label="Goal #3"
                error={errors.Goal3}
                value={data.Goal3["@value"]}
                onChange={handleChange}
                identical={compare("Goal3")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <Input
                name="Objectives3"
                label="Objectives"
                error={errors.Objectives3}
                value={data.Objectives3["@value"]}
                onChange={handleChange}
                identical={compare("Objectives3")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <Input
                name="Actions3"
                label="Planned Actions"
                error={errors.Actions3}
                value={data.Actions3["@value"]}
                onChange={handleChange}
                identical={compare("Actions3")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <Input
                name="Outcomes3"
                label="Expected Outcomes"
                error={errors.Outcomes3}
                value={data.Outcomes3["@value"]}
                onChange={handleChange}
                identical={compare("Outcomes3")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <br />
              <Input
                name="Strategies"
                label="Strategies to Ease Adjustment to Therapeutic Home"
                error={errors.Strategies}
                value={data.Strategies["@value"]}
                onChange={handleChange}
                identical={compare("Strategies")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <br />
              <Input
                name="SupervisionNeeds"
                label="Current Supervision Needs"
                error={errors.SupervisionNeeds}
                value={data.SupervisionNeeds["@value"]}
                onChange={handleChange}
                identical={compare("SupervisionNeeds")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <br />
              <Input
                name="DischargePlanning"
                label="Discharge Planning"
                error={errors.DischargePlanning}
                value={data.DischargePlanning["@value"]}
                onChange={handleChange}
                identical={compare("DischargePlanning")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
            </>
          )}
          <div>
            <div
              className="btn btn-primary m-2 d-print-none"
              onClick={() => {
                handleSubmit(null, null, null, null);
              }}
            >
              Save
            </div>
            <Signature
              canvasWidth="450"
              canvasHeight="200"
              modalTitle="Report Signatures"
              careTeams={careTeams}
              signatures={signatures}
              onSign={saveSignature}
              clientData={clientData.client}
              showModal={showSignatureModal}
              onHide={() => {
                setShowSignatureModal(false);
              }}
              finalSignType={
                formsyConfig.HelperConfiguration &&
                JSON.parse(formsyConfig.HelperConfiguration[0]).finalSignType
              }
              initialSignatureType={
                formsyConfig.HelperConfiguration &&
                JSON.parse(formsyConfig.HelperConfiguration[0]).signatureType
              }
            ></Signature>
            {submissionid ? (
              <FileUpload
                header={<div className="h5">Upload documentation here:</div>}
                objectId={submissionid}
                objectType="formsy"
                savedFiles={fileAttachments}
                printImg={true}
                loadFiles={loadFiles}
              />
            ) : null}
            <Comment
              showModal={showComment}
              commentToEdit={commentToEdit}
              onHide={() => {
                setShowComment(false);
                setCommentToEdit(undefined);
              }}
              onSave={onCommentSave}
              fields={commentFields()}
            ></Comment>
            {showShare ? (
              <ShareModal
                showShare={showShare}
                onHide={() => setShowShare(false)}
                onSend={handleShareSend}
                submissionId={submissionid}
              ></ShareModal>
            ) : null}
          </div>
        </div>
      </div>
    </>
  );
}

export default KYTFCF032;
